import { Button, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger, Popover, PopoverContent, PopoverTrigger } from '@1trading/react';
import { CalendarRange, ChevronDown } from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { boundedPeriod, type DayBounds, type Period, periodSideBounds } from '../date-bounds';
import { STATUS_FILTER_LABELS, type StatusFilter } from './grid-rows';

const STATUS_FILTERS = Object.keys(STATUS_FILTER_LABELS) as StatusFilter[];

const statusSummary = (selected: StatusFilter[]) =>
  selected.length === STATUS_FILTERS.length
    ? 'Any status'
    : selected.length
      ? STATUS_FILTERS.filter((status) => selected.includes(status))
          .map((status) => STATUS_FILTER_LABELS[status])
          .join(', ')
      : 'No status';

interface StatusFilterMenuProps {
  value: StatusFilter[];
  onChange: (value: StatusFilter[]) => void;
}

export const StatusFilterMenu = ({ value, onChange }: StatusFilterMenuProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button aria-label="Schedule status" className="h-8 w-48 justify-between text-xs font-normal" size="sm" type="button" variant="outline">
        <span className="truncate">{statusSummary(value)}</span>
        <ChevronDown className="size-4 opacity-60" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-48">
      {STATUS_FILTERS.map((status) => (
        <DropdownMenuCheckboxItem
          checked={value.includes(status)}
          className="text-xs"
          key={status}
          onCheckedChange={(checked) => onChange(checked ? [...value, status] : value.filter((item) => item !== status))}
          // Keeps the menu open so several statuses can be ticked in one go.
          onSelect={(event) => event.preventDefault()}
        >
          {STATUS_FILTER_LABELS[status]}
        </DropdownMenuCheckboxItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);
StatusFilterMenu.displayName = 'StatusFilterMenu';

const periodSummary = ({ from, to }: Period) => (from && to ? `${from} – ${to}` : from ? `From ${from}` : to ? `Until ${to}` : 'Any period');

interface PeriodFilterProps {
  value: Period;
  onChange: (value: Period) => void;
  /** The days the shown statuses can match; disabled hides the period */
  bounds: DayBounds;
}

/** Keeps the schedules in force on at least one day of the period. */
export const PeriodFilter = ({ value, onChange, bounds }: PeriodFilterProps) => {
  const applied = boundedPeriod(value, bounds);
  const sides = periodSideBounds(applied, bounds);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label="Period"
          className="h-8 w-56 justify-between gap-2 text-xs font-normal"
          disabled={bounds.disabled}
          size="sm"
          title={bounds.disabled ? bounds.reason : undefined}
          type="button"
          variant="outline"
        >
          <span className="flex items-center gap-1.5 truncate">
            <CalendarRange className="size-3.5 opacity-60" />
            {bounds.disabled ? 'Period: today' : periodSummary(applied)}
          </span>
          <ChevronDown className="size-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-72 flex-col gap-3">
        {bounds.reason ? <p className="text-xs text-muted-foreground">{bounds.reason}</p> : null}
        <div className="grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-2 text-xs">
          <span>From</span>
          <DatePicker
            clearLabel="Clear"
            max={sides.from.max}
            min={sides.from.min}
            onChange={(from) => onChange({ ...applied, from })}
            placeholder="Any start"
            value={applied.from}
          />
          <span>Until</span>
          <DatePicker clearLabel="Clear" max={sides.to.max} min={sides.to.min} onChange={(to) => onChange({ ...applied, to })} placeholder="Any end" value={applied.to} />
        </div>
        {applied.from || applied.to ? (
          <Button className="self-end" onClick={() => onChange({ from: '', to: '' })} size="sm" type="button" variant="ghost">
            Clear period
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
PeriodFilter.displayName = 'PeriodFilter';
